import axios from 'axios'
import { assert } from '../utils/assert'

class GameService {
  /**
   * Récupère la liste des jeux avec pagination et filtres
   * @param {Object} params - Paramètres de la requête
   * @param {number} params.page - Numéro de page (défaut: 1)
   * @param {number} params.limit - Nombre d'éléments par page (défaut: 10)
   * @param {Array<number>} params.versions - Liste des numéros de versions à filtrer
   * @param {string} params.language - Langue des jeux
   * @returns {Promise<Object>} Liste des jeux avec pagination
   */
  async getGames({
    page = 1,
    limit = 10,
    versions = null,
    language = null,
  } = {}) {
    const queryParams = {
      page,
      limit,
    }

    // Ajouter les filtres de version si présents
    if (versions !== null && versions.length > 0) {
      queryParams.versions = versions
    }

    // Ajouter le filtre de langue si présent
    if (language !== null) {
      queryParams.language = language
    }

    try {
      const response = await axios.get('/api/games', {
        params: queryParams,
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des jeux:', error)
      throw error
    }
  }

  /**
   * Récupère un jeu par son ID
   * @param {string} id - ID du jeu
   * @returns {Promise<Object>} Jeu
   */
  async getGame(id) {
    assert(id, 'id is required')

    try {
      const response = await axios.get(`/api/games/${id}`, {
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération du jeu:', error)
      throw error
    }
  }

  /**
   * Crée un nouveau jeu
   * @param {Object} game - Données du jeu
   * @returns {Promise<Object>} Jeu créé
   */
  async createGame(game) {
    assert(game, 'game is required')

    try {
      const response = await axios.post('/api/games', game, {
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la création du jeu:', error)
      throw error
    }
  }

  /**
   * Met à jour un jeu
   * @param {string} id - ID du jeu
   * @param {Object} game - Données du jeu à mettre à jour
   * @returns {Promise<Object>} Jeu mis à jour
   */
  async updateGame(id, game) {
    assert(id, 'id is required')
    assert(game, 'game is required')

    try {
      const response = await axios.put(`/api/games/${id}`, game, {
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la mise à jour du jeu:', error)
      throw error
    }
  }

  /**
   * Supprime un jeu
   * @param {string} id - ID du jeu
   * @returns {Promise<void>}
   */
  async deleteGame(id) {
    assert(id, 'id is required')

    try {
      await axios.delete(`/api/games/${id}`, {
        withCredentials: true,
      })
    } catch (error) {
      console.error('Erreur lors de la suppression du jeu:', error)
      throw error
    }
  }

  /**
   * Duplique un jeu
   * @param {string} id - ID du jeu à dupliquer
   * @returns {Promise<Object>} Jeu dupliqué
   */
  async duplicateGame(id) {
    assert(id, 'id is required')

    try {
      const response = await axios.post(
        `/api/games/${id}/duplicate`,
        {},
        {
          withCredentials: true,
        }
      )
      return response.data
    } catch (error) {
      console.error('Erreur lors de la duplication du jeu:', error)
      throw error
    }
  }

  /**
   * Annule une opération temporaire sur un jeu
   * @param {string} id - ID du jeu
   * @returns {Promise<void>}
   */
  async cancelGame(id) {
    assert(id, 'id is required')

    try {
      await axios.post(
        `/api/games/${id}/cancel`,
        {},
        {
          withCredentials: true,
        }
      )
    } catch (error) {
      console.error("Erreur lors de l'annulation du jeu:", error)
      throw error
    }
  }

  /**
   * Récupère les valeurs par défaut des handles
   * @returns {Promise<Object>} Valeurs par défaut des handles
   */
  async getHandlesDefaults() {
    try {
      const response = await axios.get('/api/games/handles/defaults', {
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error(
        'Erreur lors de la récupération des handles par défaut:',
        error
      )
      throw error
    }
  }

  /**
   * Récupère la liste des niveaux
   * @returns {Promise<Object>} Liste des niveaux
   */
  async getLevels() {
    try {
      const response = await axios.get('/api/games/levels', {
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des niveaux:', error)
      throw error
    }
  }
}

export default new GameService()
